/**
 * Insights vocabulary: the nova-v1 score tree labels, KPI metrics with their units and which
 * direction is good, score bands, and the period/granularity read from the URL.
 * Scores are computed by the server (ADR-0007); nothing here computes a score.
 */
import { msg, t } from '@/lib/i18n';
import { formatNumber, getDisplayTimeZone } from '@/lib/format';
import { type ResolvedRange, zonedMidnight } from '@/lib/timeRange';
import { scoreBand } from '@/ui';
import type { components } from '@/lib/api/schema';

type S = components['schemas'];
export type Summary = S['InsightSummaryOut'];
export type SeriesOut = S['InsightSeriesOut'];
export type Ranking = S['InsightRankingOut'];
export type RankingItem = S['InsightRankingItem'];
export type EntityPerformance = S['EntityPerformanceOut'];
export type GroupComparison = S['GroupComparisonOut'];
export type GroupPerformance = S['GroupPerformance'];
export type PerfCard = S['PerfCard'];
export type PerfKpis = S['PerfKpis'];
export type ScoreNode = S['InsightScoreNode'];
export type Dimension = S['InsightDimensionOut'];
export type Scores = Record<string, number | null | undefined>;

export type Entity = 'driver' | 'vehicle';
export type ScoreKey = 'safety' | 'eco';
export type Granularity = 'day' | 'week' | 'month';

/** nova-v1 score labels (server keys), translated here; the tree's shape comes from /score-tree. */
export const SCORE_LABELS: Record<string, string> = {
  safety: msg('Safety'),
  vehicle_control: msg('Vehicle control'),
  acceleration: msg('Acceleration'),
  deceleration: msg('Braking'),
  steering: msg('Cornering'),
  road_sharing: msg('Road sharing'),
  tailgating: msg('Following distance'),
  speed_management: msg('Speed management'),
  speeding: msg('Speeding'),
  attention: msg('Attention'),
  sleepiness: msg('Alertness'),
  eyes_closed: msg('Eyes closed'),
  yawning: msg('Yawning'),
  focus: msg('Focus'),
  phone_use: msg('Phone use'),
  eco: msg('Eco-efficiency'),
  standstill: msg('Standstill'),
  idling: msg('Idling'),
  engine_gear: msg('Engine & gear'),
  engine_load: msg('Engine load'),
  speed_adaptation: msg('Speed adaptation'),
  cruise_control: msg('Cruise control'),
  anticipation: msg('Anticipation'),
  coasting: msg('Coasting'),
};

export function scoreLabel(key: string, fallback?: string): string {
  const l = SCORE_LABELS[key];
  return l ? t(l) : (fallback ?? key);
}

/** Why a category has no score — the server's reasons (insights/api.py `_why`), translated. */
const REASONS: Record<string, string> = {
  vehicle_control: msg('No accelerometer or harsh-event data (or under 5 km with it).'),
  road_sharing: msg('No headway-monitoring camera or radar on this vehicle.'),
  speed_management: msg('Speed limits unknown for the roads driven (or under 10 min).'),
  attention: msg('No working driver-facing camera (or under 5 km with it).'),
  eco: msg('No engine data (CAN) — eco-efficiency is not observable.'),
};

export function reasonFor(key: string, serverReasons: Record<string, string> | null | undefined): string | undefined {
  const server = serverReasons?.[key];
  if (!server) return undefined;
  const known = REASONS[key];
  return known ? t(known) : server;
}

export const GENERIC_NOT_OBSERVABLE = msg('Not observable in this period: the sensor behind this score did not watch long enough.');

/** Exposure behind each category, so a score shows how much it rests on. */
export const CATEGORY_EXPOSURE: Record<string, { key: keyof S['PerfExposure']; unit: 'km' | 'h' }> = {
  vehicle_control: { key: 'harsh_km', unit: 'km' },
  road_sharing: { key: 'headway_h', unit: 'h' },
  speed_management: { key: 'limit_h', unit: 'h' },
  attention: { key: 'camera_km', unit: 'km' },
  standstill: { key: 'engine_data_h', unit: 'h' },
  engine_gear: { key: 'engine_data_h', unit: 'h' },
  speed_adaptation: { key: 'engine_data_h', unit: 'h' },
  anticipation: { key: 'engine_data_h', unit: 'h' },
};

export type Better = 'higher' | 'lower' | 'neutral';

export interface MetricDef {
  key: string;
  label: string;
  unit: string;
  digits: number;
  better: Better;
  /** Draw as bars (amounts) or lines (rates, scores). */
  kind: 'line' | 'bar';
}

const M = (key: string, label: string, unit: string, digits: number, better: Better, kind: 'line' | 'bar' = 'line'): MetricDef => ({
  key,
  label,
  unit,
  digits,
  better,
  kind,
});

/** KPI metrics of the server's `derived()` (all plain numbers; per-100 km events are separate). */
export const METRICS: Record<string, MetricDef> = {
  km: M('km', msg('Distance'), 'km', 0, 'neutral', 'bar'),
  driving_h: M('driving_h', msg('Driving'), 'h', 1, 'neutral', 'bar'),
  engine_h: M('engine_h', msg('Engine on'), 'h', 1, 'neutral', 'bar'),
  idle_h: M('idle_h', msg('Idling'), 'h', 1, 'lower', 'bar'),
  idle_pct: M('idle_pct', msg('Idle share'), '%', 1, 'lower'),
  pto_h: M('pto_h', msg('PTO'), 'h', 1, 'neutral', 'bar'),
  avg_speed_kmh: M('avg_speed_kmh', msg('Average speed'), 'km/h', 0, 'neutral'),
  fuel_l: M('fuel_l', msg('Fuel'), 'L', 0, 'lower', 'bar'),
  fuel_l_100km: M('fuel_l_100km', msg('Fuel per 100 km'), 'L/100 km', 1, 'lower'),
  idle_fuel_l: M('idle_fuel_l', msg('Fuel used idling'), 'L', 0, 'lower', 'bar'),
  co2_kg: M('co2_kg', msg('CO₂'), 'kg', 0, 'lower', 'bar'),
  fuel_drop_l: M('fuel_drop_l', msg('Fuel drops'), 'L', 0, 'lower', 'bar'),
  night_pct: M('night_pct', msg('Night driving'), '%', 1, 'neutral'),
  speeding_pct: M('speeding_pct', msg('Time over the limit'), '%', 1, 'lower'),
  offline_h: M('offline_h', msg('Offline'), 'h', 1, 'lower', 'bar'),
  max_speed_kmh: M('max_speed_kmh', msg('Max speed'), 'km/h', 0, 'neutral'),
};

export function metricDef(key: string): MetricDef {
  return METRICS[key] ?? (SCORE_LABELS[key] ? M(key, SCORE_LABELS[key], '', 0, 'higher') : M(key, key, '', 1, 'neutral'));
}

export function isScoreKey(key: string): boolean {
  return key in SCORE_LABELS;
}

export const EVENT_LABELS: Record<string, string> = {
  harsh: msg('Harsh events'),
  speeding: msg('Speeding events'),
  tailgating: msg('Tailgating'),
  collision_warnings: msg('Collision warnings'),
  attention: msg('Attention alerts'),
};

/** "12.3 L/100 km", "4 %", "—" for null (never 0). */
export function formatMetric(key: string, value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const d = metricDef(key);
  const n = formatNumber(value, d.digits);
  return d.unit ? `${n} ${d.unit}` : n;
}

export type DeltaSentiment = 'good' | 'bad' | 'neutral';

export function deltaOf(
  value: number | null | undefined,
  better: Better,
  digits = 1,
): { label: string; direction: 'up' | 'down' | 'flat'; sentiment: DeltaSentiment } | undefined {
  if (value == null || Number.isNaN(value)) return undefined;
  const flat = Math.abs(value) < 10 ** -digits / 2;
  const direction = flat ? 'flat' : value > 0 ? 'up' : 'down';
  const sentiment: DeltaSentiment = flat || better === 'neutral' ? 'neutral' : value > 0 === (better === 'higher') ? 'good' : 'bad';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '±';
  return { label: `${sign}${formatNumber(Math.abs(value), digits)}`, direction, sentiment };
}

/** Score bands (style reference §8.4) — for grouping, never to compute a score. */
export const BANDS = [
  { key: 'excellent', label: msg('Excellent'), range: '80–100' },
  { key: 'good', label: msg('Good'), range: '70–79' },
  { key: 'fair', label: msg('Fair'), range: '60–69' },
  { key: 'poor', label: msg('Poor'), range: '40–59' },
  { key: 'bad', label: msg('Bad'), range: '0–39' },
  { key: 'none', label: msg('Not observable'), range: '—' },
] as const;
export type BandKey = (typeof BANDS)[number]['key'];

export function bandCounts(values: (number | null | undefined)[]): Record<BandKey, number> {
  const out: Record<BandKey, number> = { excellent: 0, good: 0, fair: 0, poor: 0, bad: 0, none: 0 };
  for (const v of values) out[scoreBand(v)] += 1;
  return out;
}

export function isBandKey(v: unknown): v is BandKey {
  return typeof v === 'string' && BANDS.some((b) => b.key === v);
}

/** The period the API expects: inclusive local days, from the global time range. */
export interface Period {
  start: string;
  end: string;
  days: number;
}

export function periodOf(range: ResolvedRange): Period {
  const days = Math.max(1, Math.round((Date.parse(`${range.toDay}T00:00:00Z`) - Date.parse(`${range.fromDay}T00:00:00Z`)) / 86_400_000) + 1);
  return { start: range.fromDay, end: range.toDay, days };
}

/** Day buckets up to ~6 weeks, weeks up to ~7 months, months beyond (a readable number of bars). */
export function autoGranularity(days: number): Granularity {
  if (days <= 45) return 'day';
  if (days <= 210) return 'week';
  return 'month';
}

export function isGranularity(v: unknown): v is Granularity {
  return v === 'day' || v === 'week' || v === 'month';
}

export const GRANULARITY_LABELS: Record<Granularity, string> = { day: msg('Per day'), week: msg('Per week'), month: msg('Per month') };

/** Noon of a local calendar day (company timezone) as an instant, so the axis shows that day. */
export function dayInstant(isoDay: string, tz: string = getDisplayTimeZone()): number {
  const [y, m, d] = isoDay.slice(0, 10).split('-').map(Number);
  return zonedMidnight({ y: y ?? 1970, m: m ?? 1, d: d ?? 1 }, tz).getTime() + 12 * 3_600_000;
}

/** Series from the API as chart points on the real calendar axis (null stays a gap). */
export function seriesPoints(axis: string[], values: (number | null)[] | undefined, tz?: string): [number, number | null][] {
  return axis.map((d, i) => [dayInstant(d, tz), values?.[i] ?? null]);
}
